import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectValue 
} from '@/components/ui/select';
import { 
  SelectTriggerWithChildren, 
  SelectContentWithChildren, 
  SelectItemWithChildren 
} from '@/components/ui/select-with-children';
import { ScrollAreaWithChildren } from '@/components/ui/scroll-area-with-children';
import { LabelWithChildren } from '@/components/ui/label-with-children';
import { fundamentalAnalysisService, FundamentalData } from '@/services/fundamentalAnalysisService';

interface FundamentalAnalysisPanelProps {
  symbol: string;
  data?: any;
}

const FundamentalAnalysisPanel: React.FC<FundamentalAnalysisPanelProps> = ({ symbol, data }) => {
  const [period, setPeriod] = useState('quarterly');
  const [fundamentalData, setFundamentalData] = useState<FundamentalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!symbol) return;
      
      setLoading(true);
      setError(null);
      try {
        const data = await fundamentalAnalysisService.getFundamentalData(symbol, period);
        setFundamentalData(data);
      } catch (e: any) {
        console.error('Error fetching fundamental data:', e);
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [symbol, period]);
  
  const formatValue = (value: number | string): string => {
    return fundamentalAnalysisService.formatFinancialValue(value);
  };
  
  return (
    <Card className="w-full h-full bg-white shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">{symbol} Fundamentals</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <LabelWithChildren>Time Period</LabelWithChildren>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTriggerWithChildren className="w-[180px]">
                <SelectValue placeholder="Select period" />
              </SelectTriggerWithChildren>
              <SelectContentWithChildren>
                <SelectItemWithChildren value="quarterly">Quarterly</SelectItemWithChildren>
                <SelectItemWithChildren value="annual">Annual</SelectItemWithChildren>
                <SelectItemWithChildren value="ttm">Trailing 12 Months</SelectItemWithChildren>
              </SelectContentWithChildren>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading data...</div>
          ) : error ? (
            <div className="text-center py-4 text-red-500">Error: {error}</div>
          ) : fundamentalData ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium">Company Overview</h4>
                  <div className="space-y-2 mt-2">
                    <p className="text-xs text-gray-500">Name: {fundamentalData.overview.Name || symbol}</p>
                    <p className="text-xs text-gray-500">Sector: {fundamentalData.overview.Sector || 'N/A'}</p>
                    <p className="text-xs text-gray-500">Industry: {fundamentalData.overview.Industry || 'N/A'}</p>
                    <p className="text-xs text-gray-500">Market Cap: {fundamentalData.overview.MarketCapitalization ? 
                      formatValue(fundamentalData.overview.MarketCapitalization) : 'N/A'}</p>
                    <p className="text-xs text-gray-500">P/E Ratio: {fundamentalData.overview.PERatio || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium">Income Statement</h4>
                  {fundamentalData.incomeStatement.length > 0 ? (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-gray-500">Date: {fundamentalData.incomeStatement[0].date || 'N/A'}</p>
                      <p className="text-xs text-gray-500">Revenue: ${formatValue(fundamentalData.incomeStatement[0].revenue)}</p>
                      <p className="text-xs text-gray-500">Gross Profit: ${formatValue(fundamentalData.incomeStatement[0].grossProfit)}</p>
                      <p className="text-xs text-gray-500">Net Income: ${formatValue(fundamentalData.incomeStatement[0].netIncome)}</p>
                      <p className="text-xs text-gray-500">EPS: ${typeof fundamentalData.incomeStatement[0].eps === 'number' ? 
                        fundamentalData.incomeStatement[0].eps.toFixed(2) : 'N/A'}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mt-2">No income statement data available</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium">Balance Sheet</h4>
                  {fundamentalData.balanceSheet.length > 0 ? (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-gray-500">Date: {fundamentalData.balanceSheet[0].date || 'N/A'}</p>
                      <p className="text-xs text-gray-500">Total Assets: ${formatValue(fundamentalData.balanceSheet[0].totalAssets)}</p>
                      <p className="text-xs text-gray-500">Total Liabilities: ${formatValue(fundamentalData.balanceSheet[0].totalLiabilities)}</p>
                      <p className="text-xs text-gray-500">Shareholder Equity: ${formatValue(fundamentalData.balanceSheet[0].totalShareholderEquity)}</p>
                      <p className="text-xs text-gray-500">Cash & Equivalents: ${formatValue(fundamentalData.balanceSheet[0].cashAndEquivalents)}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mt-2">No balance sheet data available</p>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-medium">Cash Flow</h4>
                  {fundamentalData.cashFlow.length > 0 ? (
                    <div className="space-y-2 mt-2">
                      <p className="text-xs text-gray-500">Date: {fundamentalData.cashFlow[0].date || 'N/A'}</p>
                      <p className="text-xs text-gray-500">Operating Cash Flow: ${formatValue(fundamentalData.cashFlow[0].operatingCashFlow)}</p>
                      <p className="text-xs text-gray-500">Capital Expenditures: ${formatValue(fundamentalData.cashFlow[0].capitalExpenditures)}</p>
                      <p className="text-xs text-gray-500">Investing Cash Flow: ${formatValue(fundamentalData.cashFlow[0].cashflowFromInvestment)}</p>
                      <p className="text-xs text-gray-500">Financing Cash Flow: ${formatValue(fundamentalData.cashFlow[0].cashflowFromFinancing)}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 mt-2">No cash flow data available</p>
                  )}
                </div>
              </div>

              {fundamentalData.overview.Description && (
                <div>
                  <h4 className="text-sm font-medium">Company Description</h4>
                  <ScrollAreaWithChildren className="h-[150px] rounded border p-4 mt-2">
                    <p className="text-xs text-gray-500">{fundamentalData.overview.Description}</p>
                  </ScrollAreaWithChildren>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">No data available</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FundamentalAnalysisPanel;